// 統一通知中心 store module（namespaced: notifications）
import {
  Notification_GetUnreadCount,
  Notification_GetList,
  Notification_MarkRead,
  Notification_MarkAllRead,
} from "@/apis/api";

/**
 * 未讀數的輪詢間隔。
 *
 * 沒有長連線也沒有 Web Push，瀏覽器端唯一的更新來源就是這個輪詢 ——
 * 45 秒太久，使用者的體感是「要重新整理才看得到通知」。
 *
 * 代價很小：unread-count 只回一個數字，而且背景分頁會跳過（見 startPolling），
 * 所以真正在打的只有「使用者正在看」的分頁。真的要即時就得上 SSE 或
 * Web Push，那是另一個層級的工程。
 */
const POLL_INTERVAL_MS = 10000;

// fetchList 的請求序號（見 fetchList）
let listRequestSeq = 0;

const state = () => ({
  unreadCount: 0,
  items: [],
  total: 0,
  page: 1,
  pageSize: 20,
  loading: false,
  _pollTimer: null,
  // visibilitychange 的監聽器。存起來是為了 stopPolling 時拆得掉 ——
  // 不拆的話登出再登入會愈疊愈多個
  _visibilityHandler: null,
});

const mutations = {
  SET_UNREAD(state, n) {
    state.unreadCount = Number(n) || 0;
  },
  SET_LIST(state, { items, total, unreadCount, page, pageSize }) {
    state.items = items || [];
    state.total = total || 0;
    state.unreadCount = Number(unreadCount) || 0;
    state.page = page || 1;
    state.pageSize = pageSize || 20;
  },
  MARK_ONE_READ(state, { id, wasUnread }) {
    const it = state.items.find((x) => x.id === id);
    if (it && !it.isRead) {
      it.isRead = true;
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    } else if (!it && wasUnread) {
      // 通知中心自己管清單（搜尋結果不放 store），那邊點的未讀通知要靠呼叫端告知
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    }
  },
  MARK_ALL_READ(state) {
    state.items.forEach((x) => (x.isRead = true));
    state.unreadCount = 0;
  },
  SET_LOADING(state, v) {
    state.loading = !!v;
  },
  SET_TIMER(state, t) {
    state._pollTimer = t;
  },
  SET_VISIBILITY_HANDLER(state, h) {
    state._visibilityHandler = h;
  },
};

const actions = {
  async fetchUnreadCount({ commit, rootState }) {
    if (!rootState.signinStatus) return;
    try {
      const res = await Notification_GetUnreadCount();
      commit("SET_UNREAD", res.data?.data?.unreadCount ?? 0);
    } catch (e) {
      /* 靜默失敗，不打擾 */
    }
  },

  async fetchList({ commit, rootState }, { page = 1, pageSize = 20, onlyUnread = false, source = null, keyword = null } = {}) {
    if (!rootState.signinStatus) return;
    // 搜尋邊打邊查時，先送出的請求可能比較晚回來 —— 只採用最後一次的結果
    const seq = ++listRequestSeq;
    commit("SET_LOADING", true);
    try {
      const res = await Notification_GetList({ page, pageSize, onlyUnread, source, keyword: keyword || undefined });
      if (seq !== listRequestSeq) return;
      const d = res.data?.data || {};
      commit("SET_LIST", {
        items: d.items || [],
        total: d.total || 0,
        unreadCount: d.unreadCount || 0,
        page: d.page || page,
        pageSize: d.pageSize || pageSize,
      });
    } catch (e) {
      /* 靜默 */
    } finally {
      if (seq === listRequestSeq) commit("SET_LOADING", false);
    }
  },

  // payload：通知 id，或 { id, wasUnread }（通知中心用，見 MARK_ONE_READ）
  async markRead({ commit }, payload) {
    const id = typeof payload === "object" && payload !== null ? payload.id : payload;
    const wasUnread = typeof payload === "object" && payload !== null ? !!payload.wasUnread : false;
    commit("MARK_ONE_READ", { id, wasUnread }); // 樂觀更新
    try {
      await Notification_MarkRead(id);
    } catch (e) {
      /* 失敗不還原，下次輪詢會校正 */
    }
  },

  async markAllRead({ commit }) {
    commit("MARK_ALL_READ");
    try {
      await Notification_MarkAllRead();
    } catch (e) {
      /* 失敗下次輪詢校正 */
    }
  },

  startPolling({ state, commit, dispatch }) {
    if (state._pollTimer) return; // 已在輪詢
    dispatch("fetchUnreadCount");

    const t = setInterval(() => {
      // 分頁在背景就不打 —— 使用者看不到鈴鐺，算出來的數字也沒人看。
      // 切回來時下面的 visibilitychange 會立刻補一次，不會漏。
      if (typeof document !== "undefined" && document.hidden) return;
      dispatch("fetchUnreadCount");
    }, POLL_INTERVAL_MS);
    commit("SET_TIMER", t);

    /*
      切回分頁就立刻更新。

      這比縮短輪詢間隔更有感：使用者的習慣是切去別的分頁做事、切回來看，
      而切回來的當下很可能剛好在輪詢週期的開頭，還要再等一輪。
    */
    if (typeof document !== "undefined" && !state._visibilityHandler) {
      const handler = () => {
        if (!document.hidden) dispatch("fetchUnreadCount");
      };
      document.addEventListener("visibilitychange", handler);
      commit("SET_VISIBILITY_HANDLER", handler);
    }
  },

  stopPolling({ state, commit }) {
    if (state._pollTimer) {
      clearInterval(state._pollTimer);
      commit("SET_TIMER", null);
    }
    if (state._visibilityHandler && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", state._visibilityHandler);
      commit("SET_VISIBILITY_HANDLER", null);
    }
    commit("SET_UNREAD", 0);
  },
};

export default { namespaced: true, state, mutations, actions };
